// src/screens/GameAvailabilityScreen.js
// Admin screen to enable/disable games from appearing in CreateEventsScreen
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Switch,
  ActivityIndicator,
  TextInput,
  Modal,
  Pressable,
} from 'react-native';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { GAME_PROFILES } from '../constants/gameData';

import { db } from '../../firebaseConfig';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#111',
  GOLD: '#FCD000',
  TEXT: '#fff',
  DIM: '#888',
  BORDER: '#222',
  SUCCESS: '#2ECC71',
  DANGER: '#E74C3C',
};

export default function GameAvailabilityScreen({ navigation }) {
  const { user, isAdmin, isLoading: authLoading } = useAdminAuth();
  
  // Game availability state - initialize from GAME_PROFILES
  const [games, setGames] = useState({});
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Modal state for disable reason
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);
  const [disableReason, setDisableReason] = useState('');

  // Confirmation modal state (replaces Alert.alert for web compatibility)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmButtonText, setConfirmButtonText] = useState('Confirm');
  const [confirmButtonStyle, setConfirmButtonStyle] = useState('danger');

  // Initialize games from GAME_PROFILES and load any saved state from Firestore
  useEffect(() => {
    if (authLoading || !isAdmin) return;

    // First, initialize all games from GAME_PROFILES as enabled by default
    const initialGames = {};
    Object.keys(GAME_PROFILES).forEach(gameKey => {
      const profile = GAME_PROFILES[gameKey];
      initialGames[gameKey] = {
        displayName: profile.displayName || gameKey,
        enabled: true,
        visibility: 'live',
        reason: '',
        category: profile.type || 'other',
      };
    });

    // Then try to load saved state from Firestore
    const docRef = doc(db, 'config', 'gameAvailability');
    
    const unsubscribe = onSnapshot(docRef, 
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          // Merge Firestore data with initial games
          const mergedGames = { ...initialGames };
          if (data.games) {
            Object.keys(data.games).forEach(gameKey => {
              if (mergedGames[gameKey]) {
                mergedGames[gameKey] = {
                  ...mergedGames[gameKey],
                  ...data.games[gameKey],
                };
              }
            });
          }
          setGames(mergedGames);
        } else {
          // Document doesn't exist, use initial games
          setGames(initialGames);
        }
        setLoading(false);
      },
      (error) => {
        console.warn('Error loading game availability:', error);
        // On error, just use initial games from GAME_PROFILES
        setGames(initialGames);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authLoading, isAdmin]);

  // Save game state to Firestore
  const saveToFirestore = async (updatedGames) => {
    try {
      const docRef = doc(db, 'config', 'gameAvailability');
      const docSnap = await getDoc(docRef);
      
      const firestoreGames = {};
      Object.keys(updatedGames).forEach(gameKey => {
        firestoreGames[gameKey] = {
          displayName: updatedGames[gameKey].displayName,
          enabled: updatedGames[gameKey].enabled,
          visibility: updatedGames[gameKey].visibility || 'live',
          reason: updatedGames[gameKey].reason || '',
          updatedAt: new Date().toISOString(),
          updatedBy: user?.uid || 'unknown',
        };
      });

      if (docSnap.exists()) {
        await updateDoc(docRef, {
          games: firestoreGames,
          lastUpdated: new Date(),
          lastUpdatedBy: user?.uid,
        });
      } else {
        await setDoc(docRef, {
          games: firestoreGames,
          lastUpdated: new Date(),
          lastUpdatedBy: user?.uid,
        });
      }
      console.log('Game availability saved successfully');
    } catch (error) {
      console.error('Error saving to Firestore:', error);
      // Show error in a simple way
      setConfirmTitle('Error');
      setConfirmMessage('Failed to save changes: ' + error.message);
      setConfirmButtonText('OK');
      setConfirmButtonStyle('primary');
      setConfirmAction(null);
      setShowConfirmModal(true);
    }
  };

  // Toggle a game's availability
  const handleToggle = (gameKey, newValue) => {
    if (newValue === false) {
      // Disabling - ask for reason
      setPendingToggle(gameKey);
      setDisableReason('');
      setShowReasonModal(true);
    } else {
      // Enabling - just toggle
      const updatedGames = {
        ...games,
        [gameKey]: {
          ...games[gameKey],
          enabled: true,
          reason: '',
        },
      };
      setGames(updatedGames);
      saveToFirestore(updatedGames);
    }
  };

  // Change a game's visibility (live / testing / hidden)
  const handleVisibilityChange = (gameKey, newVisibility) => {
    const updatedGames = {
      ...games,
      [gameKey]: {
        ...games[gameKey],
        visibility: newVisibility,
      },
    };
    setGames(updatedGames);
    saveToFirestore(updatedGames);
  };

  // Confirm disable with reason
  const confirmDisable = () => {
    if (!pendingToggle) return;
    
    const updatedGames = {
      ...games,
      [pendingToggle]: {
        ...games[pendingToggle],
        enabled: false,
        reason: disableReason || 'No reason provided',
      },
    };
    setGames(updatedGames);
    saveToFirestore(updatedGames);
    
    setShowReasonModal(false);
    setPendingToggle(null);
    setDisableReason('');
  };

  // Show confirmation modal helper
  const showConfirmation = (title, message, buttonText, style, action) => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmButtonText(buttonText);
    setConfirmButtonStyle(style);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  // Emergency disable all
  const handleEmergencyDisable = () => {
    showConfirmation(
      'Emergency Disable All Games',
      'This will disable ALL games immediately. Users will not be able to create any events. Are you sure?',
      'Disable All',
      'danger',
      () => {
        const updatedGames = { ...games };
        Object.keys(updatedGames).forEach(key => {
          updatedGames[key] = {
            ...updatedGames[key],
            enabled: false,
            reason: 'Emergency disable',
          };
        });
        setGames(updatedGames);
        saveToFirestore(updatedGames);
        setShowConfirmModal(false);
      }
    );
  };

  // Enable all games
  const handleEnableAll = () => {
    showConfirmation(
      'Enable All Games',
      'This will enable ALL games for event creation. Are you sure?',
      'Enable All',
      'success',
      () => {
        const updatedGames = { ...games };
        Object.keys(updatedGames).forEach(key => {
          updatedGames[key] = {
            ...updatedGames[key],
            enabled: true,
            reason: '',
          };
        });
        setGames(updatedGames);
        saveToFirestore(updatedGames);
        setShowConfirmModal(false);
      }
    );
  };

  // Filter games by category
  const filteredGames = Object.entries(games).filter(([key, game]) => {
    if (categoryFilter === 'all') return true;
    return game.category === categoryFilter;
  });

  // Count stats
  const enabledCount = Object.values(games).filter(g => g.enabled).length;
  const disabledCount = Object.values(games).filter(g => !g.enabled).length;
  const testingCount = Object.values(games).filter(g => g.visibility === 'testing').length;
  const totalCount = Object.keys(games).length;

  // Categories
  const categories = ['all', 'fighting', 'sports', 'racing', 'shooter', 'moba', 'rts'];

  if (authLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centerWrap}>
          <ActivityIndicator size="large" color={COLORS.GOLD} />
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centerWrap}>
          <Text style={styles.errorText}>Access Denied</Text>
          <TouchableOpacity onPress={() => navigation.navigate('Lobby')}>
            <Text style={styles.linkText}>Return to Lobby</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
        
        {/* Back Link */}
        <TouchableOpacity onPress={() => navigation.navigate('AdminDashboard')}>
          <Text style={styles.backLink}>{'<'} Back to Admin Dashboard</Text>
        </TouchableOpacity>

        {/* Title */}
        <Text style={styles.title}>Game Availability</Text>
        <Text style={styles.subtitle}>Control which games users can select when creating events</Text>

        {/* Stats */}
        <View style={styles.statsRow}>
          <View style={styles.statBox}>
            <Text style={[styles.statNumber, { color: COLORS.SUCCESS }]}>{enabledCount}</Text>
            <Text style={styles.statLabel}>Enabled</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={[styles.statNumber, { color: COLORS.DANGER }]}>{disabledCount}</Text>
            <Text style={styles.statLabel}>Disabled</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={[styles.statNumber, { color: '#f59e0b' }]}>{testingCount}</Text>
            <Text style={styles.statLabel}>Testing</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statNumber}>{totalCount}</Text>
            <Text style={styles.statLabel}>Total</Text>
          </View>
        </View>

        {/* Category Filter */}
        <Text style={styles.sectionTitle}>FILTER BY CATEGORY</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
          <View style={styles.categoryRow}>
            {categories.map(cat => (
              <TouchableOpacity
                key={cat}
                style={[styles.categoryPill, categoryFilter === cat && styles.categoryPillActive]}
                onPress={() => setCategoryFilter(cat)}
              >
                <Text style={[styles.categoryText, categoryFilter === cat && styles.categoryTextActive]}>
                  {cat === 'all' ? 'All Games' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>

        {/* Games List */}
        {loading ? (
          <ActivityIndicator size="large" color={COLORS.GOLD} style={{ marginTop: 40 }} />
        ) : (
          <View style={styles.gamesList}>
            {filteredGames.map(([gameKey, game]) => (
              <View key={gameKey} style={styles.gameRow}>
                <View style={styles.gameInfo}>
                  <View style={styles.gameNameRow}>
                    <Text style={styles.gameName}>{game.displayName}</Text>
                    {game.visibility === 'testing' && (
                      <View style={styles.testingBadge}>
                        <Text style={styles.testingBadgeText}>TESTING</Text>
                      </View>
                    )}
                    {game.visibility === 'hidden' && (
                      <View style={[styles.testingBadge, styles.hiddenBadge]}>
                        <Text style={styles.testingBadgeText}>HIDDEN</Text>
                      </View>
                    )}
                  </View>
                  <Text style={styles.gameCategory}>{game.category}</Text>
                  {!game.enabled && game.reason && (
                    <Text style={styles.gameReason}>Reason: {game.reason}</Text>
                  )}
                  {/* Visibility Picker */}
                  <View style={styles.visibilityRow}>
                    {['live', 'testing', 'hidden'].map((vis) => (
                      <TouchableOpacity
                        key={vis}
                        style={[
                          styles.visibilityPill,
                          game.visibility === vis && styles.visibilityPillActive,
                          vis === 'testing' && game.visibility === vis && styles.visibilityPillTesting,
                          vis === 'hidden' && game.visibility === vis && styles.visibilityPillHidden,
                        ]}
                        onPress={() => handleVisibilityChange(gameKey, vis)}
                      >
                        <Text style={[
                          styles.visibilityPillText,
                          game.visibility === vis && styles.visibilityPillTextActive,
                        ]}>
                          {vis.charAt(0).toUpperCase() + vis.slice(1)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                <Switch
                  value={game.enabled}
                  onValueChange={(val) => handleToggle(gameKey, val)}
                  trackColor={{ false: '#333', true: COLORS.SUCCESS }}
                  thumbColor={game.enabled ? '#fff' : '#888'}
                />
              </View>
            ))}
          </View>
        )}

        {/* Emergency Controls */}
        <Text style={[styles.sectionTitle, { marginTop: 30 }]}>EMERGENCY CONTROLS</Text>
        <TouchableOpacity style={styles.emergencyBtn} onPress={handleEmergencyDisable}>
          <Text style={styles.emergencyBtnText}>Disable All Games</Text>
        </TouchableOpacity>
        <Text style={styles.emergencyNote}>
          Use this only when critical issues require immediately stopping all event creation
        </Text>

        {disabledCount > 0 && (
          <TouchableOpacity style={styles.enableAllBtn} onPress={handleEnableAll}>
            <Text style={styles.enableAllBtnText}>Enable All Games</Text>
          </TouchableOpacity>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* Disable Reason Modal */}
      <Modal visible={showReasonModal} transparent animationType="fade">
        <Pressable style={styles.modalBackdrop} onPress={() => setShowReasonModal(false)}>
          <Pressable style={styles.modalContent} onPress={e => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Disable Game</Text>
            <Text style={styles.modalSubtitle}>
              Why are you disabling {pendingToggle ? games[pendingToggle]?.displayName : 'this game'}?
            </Text>
            <TextInput
              style={styles.modalInput}
              placeholder="Enter reason (optional)..."
              placeholderTextColor="#666"
              value={disableReason}
              onChangeText={setDisableReason}
              multiline
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={styles.modalCancelBtn} 
                onPress={() => setShowReasonModal(false)}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalConfirmBtn} onPress={confirmDisable}>
                <Text style={styles.modalConfirmText}>Disable</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Confirmation Modal (replaces Alert.alert for web) */}
      <Modal visible={showConfirmModal} transparent animationType="fade">
        <Pressable style={styles.modalBackdrop} onPress={() => setShowConfirmModal(false)}>
          <Pressable style={styles.modalContent} onPress={e => e.stopPropagation()}>
            <Text style={styles.modalTitle}>{confirmTitle}</Text>
            <Text style={styles.modalSubtitle}>{confirmMessage}</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={styles.modalCancelBtn} 
                onPress={() => setShowConfirmModal(false)}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[
                  styles.modalConfirmBtn,
                  confirmButtonStyle === 'success' && { backgroundColor: COLORS.SUCCESS },
                  confirmButtonStyle === 'primary' && { backgroundColor: COLORS.GOLD },
                ]} 
                onPress={() => {
                  if (confirmAction) {
                    confirmAction();
                  } else {
                    setShowConfirmModal(false);
                  }
                }}
              >
                <Text style={[
                  styles.modalConfirmText,
                  confirmButtonStyle === 'primary' && { color: '#000' },
                ]}>
                  {confirmButtonText}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.BG,
  },
  scroll: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 100,
  },
  centerWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: COLORS.DANGER,
    fontSize: 18,
    fontWeight: '700',
  },
  linkText: {
    color: COLORS.GOLD,
    marginTop: 12,
    textDecorationLine: 'underline',
  },
  backLink: {
    color: COLORS.DIM,
    fontSize: 14,
    marginBottom: 16,
  },
  title: {
    color: COLORS.GOLD,
    fontSize: 28,
    fontWeight: '800',
    marginBottom: 4,
  },
  subtitle: {
    color: COLORS.DIM,
    fontSize: 14,
    marginBottom: 20,
  },
  
  // Stats
  statsRow: {
    flexDirection: 'row',
    marginBottom: 24,
  },
  statBox: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 16,
    backgroundColor: COLORS.CARD,
    borderRadius: 8,
    marginHorizontal: 4,
  },
  statNumber: {
    color: COLORS.TEXT,
    fontSize: 28,
    fontWeight: '700',
  },
  statLabel: {
    color: COLORS.DIM,
    fontSize: 12,
    marginTop: 4,
  },

  // Category filter
  sectionTitle: {
    color: COLORS.DIM,
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 10,
    letterSpacing: 1,
  },
  categoryScroll: {
    marginBottom: 20,
  },
  categoryRow: {
    flexDirection: 'row',
    gap: 8,
  },
  categoryPill: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: COLORS.CARD,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  categoryPillActive: {
    backgroundColor: COLORS.GOLD,
    borderColor: COLORS.GOLD,
  },
  categoryText: {
    color: COLORS.TEXT,
    fontSize: 14,
  },
  categoryTextActive: {
    color: '#000',
    fontWeight: '600',
  },

  // Games list
  gamesList: {
    marginTop: 10,
  },
  gameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  gameInfo: {
    flex: 1,
  },
  gameName: {
    color: COLORS.TEXT,
    fontSize: 16,
    fontWeight: '600',
  },
  gameCategory: {
    color: COLORS.DIM,
    fontSize: 12,
    marginTop: 2,
    textTransform: 'capitalize',
  },
  gameReason: {
    color: COLORS.DANGER,
    fontSize: 11,
    marginTop: 4,
    fontStyle: 'italic',
  },
  gameNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  testingBadge: {
    backgroundColor: '#f59e0b',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  hiddenBadge: {
    backgroundColor: '#555',
  },
  testingBadgeText: {
    color: '#000',
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  visibilityRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 8,
  },
  visibilityPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    backgroundColor: COLORS.CARD,
  },
  visibilityPillActive: {
    backgroundColor: COLORS.SUCCESS,
    borderColor: COLORS.SUCCESS,
  },
  visibilityPillTesting: {
    backgroundColor: '#f59e0b',
    borderColor: '#f59e0b',
  },
  visibilityPillHidden: {
    backgroundColor: '#555',
    borderColor: '#555',
  },
  visibilityPillText: {
    color: COLORS.DIM,
    fontSize: 11,
    fontWeight: '600',
  },
  visibilityPillTextActive: {
    color: '#000',
  },

  // Emergency controls
  emergencyBtn: {
    backgroundColor: COLORS.DANGER,
    borderRadius: 10,
    padding: 16,
    alignItems: 'center',
  },
  emergencyBtnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  emergencyNote: {
    color: COLORS.DIM,
    fontSize: 12,
    textAlign: 'center',
    marginTop: 10,
  },
  enableAllBtn: {
    backgroundColor: COLORS.SUCCESS,
    borderRadius: 10,
    padding: 16,
    alignItems: 'center',
    marginTop: 16,
  },
  enableAllBtnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },

  // Modal
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#1a1a1a',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    color: COLORS.GOLD,
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
  },
  modalSubtitle: {
    color: COLORS.DIM,
    fontSize: 14,
    marginBottom: 16,
    lineHeight: 20,
  },
  modalInput: {
    backgroundColor: '#0a0a0a',
    borderRadius: 8,
    padding: 12,
    color: COLORS.TEXT,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  modalButtons: {
    flexDirection: 'row',
    marginTop: 20,
    gap: 12,
  },
  modalCancelBtn: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    alignItems: 'center',
  },
  modalCancelText: {
    color: COLORS.DIM,
    fontSize: 15,
    fontWeight: '600',
  },
  modalConfirmBtn: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    backgroundColor: COLORS.DANGER,
    alignItems: 'center',
  },
  modalConfirmText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});